import { apiRequest } from "../client";
import type {
  BrandingConfig,
  BrandingPolicy,
  BrandingScope,
  BrandingUploadResult,
  BusinessUnit,
  CompanyBrandingAdmin,
  EffectiveBranding,
  HomeLogoVariant,
} from "./types";

const BASE = "/agentic-v2";

export const brandingKeys = {
  all: ["branding"] as const,
  resolved: (asUserId?: string, surfaceId?: string) =>
    [...brandingKeys.all, "resolved", asUserId ?? "self", surfaceId ?? "default"] as const,
  admin: (companyId: string) => [...brandingKeys.all, "admin", companyId] as const,
};

export async function getResolvedBranding(params?: {
  asUserId?: string;
  surfaceId?: string;
}): Promise<EffectiveBranding> {
  const query: Record<string, string> = {};
  if (params?.asUserId) query.as_user_id = params.asUserId;
  if (params?.surfaceId) query.surface_id = params.surfaceId;
  return apiRequest<EffectiveBranding>({
    method: "GET",
    url: `${BASE}/surfaces/branding/resolved`,
    params: query,
  });
}

export async function getCompanyBrandingAdmin(
  companyId: string,
): Promise<CompanyBrandingAdmin> {
  return apiRequest<CompanyBrandingAdmin>({
    method: "GET",
    url: `${BASE}/admin/companies/${encodeURIComponent(companyId)}/branding`,
  });
}

export async function putCompanyBrandingAdmin(
  companyId: string,
  body: {
    branding?: BrandingConfig | null;
    branding_policy?: BrandingPolicy;
  },
): Promise<CompanyBrandingAdmin> {
  return apiRequest<CompanyBrandingAdmin>({
    method: "PUT",
    url: `${BASE}/admin/companies/${encodeURIComponent(companyId)}/branding`,
    data: body,
  });
}

export async function createBusinessUnit(
  companyId: string,
  body: { business_unit_id: string; label: string },
): Promise<BusinessUnit> {
  return apiRequest<BusinessUnit>({
    method: "POST",
    url: `${BASE}/admin/companies/${encodeURIComponent(companyId)}/business-units`,
    data: body,
  });
}

export async function updateBusinessUnit(
  companyId: string,
  businessUnitId: string,
  body: { label?: string; branding?: BrandingConfig | null; clear_branding?: boolean },
): Promise<BusinessUnit> {
  return apiRequest<BusinessUnit>({
    method: "PUT",
    url: `${BASE}/admin/companies/${encodeURIComponent(companyId)}/business-units/${encodeURIComponent(businessUnitId)}`,
    data: body,
  });
}

export async function deleteBusinessUnit(
  companyId: string,
  businessUnitId: string,
): Promise<void> {
  await apiRequest<void>({
    method: "DELETE",
    url: `${BASE}/admin/companies/${encodeURIComponent(companyId)}/business-units/${encodeURIComponent(businessUnitId)}`,
  });
}

export async function uploadBrandingLogo(
  companyId: string,
  file: File,
  variant: HomeLogoVariant,
  scope: BrandingScope = "company",
  businessUnitId?: string,
): Promise<BrandingUploadResult> {
  const formData = new FormData();
  formData.append("file", file);
  const params: Record<string, string> = { scope };
  if (businessUnitId) params.business_unit_id = businessUnitId;
  return apiRequest<BrandingUploadResult>({
    method: "POST",
    url: `${BASE}/admin/companies/${encodeURIComponent(companyId)}/branding/logo/${variant}`,
    data: formData,
    params,
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export async function deleteBrandingLogo(
  companyId: string,
  variant: HomeLogoVariant,
  scope: BrandingScope = "company",
  businessUnitId?: string,
): Promise<void> {
  const params: Record<string, string> = { scope };
  if (businessUnitId) params.business_unit_id = businessUnitId;
  await apiRequest<void>({
    method: "DELETE",
    url: `${BASE}/admin/companies/${encodeURIComponent(companyId)}/branding/logo/${variant}`,
    params,
  });
}

export async function uploadBrandingBackgroundImage(
  companyId: string,
  file: File,
  variant: HomeLogoVariant,
  scope: BrandingScope = "company",
  businessUnitId?: string,
): Promise<BrandingUploadResult> {
  const formData = new FormData();
  formData.append("file", file);
  const params: Record<string, string> = { scope };
  if (businessUnitId) params.business_unit_id = businessUnitId;
  return apiRequest<BrandingUploadResult>({
    method: "POST",
    url: `${BASE}/admin/companies/${encodeURIComponent(companyId)}/branding/background/image/${variant}`,
    data: formData,
    params,
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export async function deleteBrandingBackgroundImage(
  companyId: string,
  variant: HomeLogoVariant,
  scope: BrandingScope = "company",
  businessUnitId?: string,
): Promise<void> {
  const params: Record<string, string> = { scope };
  if (businessUnitId) params.business_unit_id = businessUnitId;
  await apiRequest<void>({
    method: "DELETE",
    url: `${BASE}/admin/companies/${encodeURIComponent(companyId)}/branding/background/image/${variant}`,
    params,
  });
}

export async function uploadBrandingWatermarkLogo(
  companyId: string,
  file: File,
  scope: BrandingScope = "company",
  businessUnitId?: string,
): Promise<BrandingUploadResult> {
  const formData = new FormData();
  formData.append("file", file);
  const params: Record<string, string> = { scope };
  if (businessUnitId) params.business_unit_id = businessUnitId;
  return apiRequest<BrandingUploadResult>({
    method: "POST",
    url: `${BASE}/admin/companies/${encodeURIComponent(companyId)}/branding/watermark/logo`,
    data: formData,
    params,
    headers: { "Content-Type": "multipart/form-data" },
  });
}

export async function deleteBrandingWatermarkLogo(
  companyId: string,
  scope: BrandingScope = "company",
  businessUnitId?: string,
): Promise<void> {
  const params: Record<string, string> = { scope };
  if (businessUnitId) params.business_unit_id = businessUnitId;
  await apiRequest<void>({
    method: "DELETE",
    url: `${BASE}/admin/companies/${encodeURIComponent(companyId)}/branding/watermark/logo`,
    params,
  });
}
